import {
  buildFormSchemaForCarriers,
  buildPortalContractSchemaForCarriers,
} from "@aam/shared";

export interface NormalizedDraft {
  formData: Record<string, unknown>;
  currentStep: number;
}

/** Marker stored in portal draft formData after the identity-less schema. */
export const PORTAL_FORM_VARIANT = "portal";

const IDENTITY_FIELD_IDS = ["fullName", "email", "phone"] as const;

/** Compose fullName from legacy firstName/lastName drafts when it's missing. */
function backfillFullName(formData: Record<string, unknown>) {
  if (typeof formData.fullName === "string" && formData.fullName.trim()) return;
  const firstName = typeof formData.firstName === "string" ? formData.firstName.trim() : "";
  const lastName = typeof formData.lastName === "string" ? formData.lastName.trim() : "";
  const composed = [firstName, lastName].filter(Boolean).join(" ");
  if (composed) formData.fullName = composed;
}

function hasSelectedCarriers(formData: Record<string, unknown>) {
  const carriers = formData.selectedCarriers;
  return Array.isArray(carriers) && carriers.length > 0;
}

function identityComplete(formData: Record<string, unknown>) {
  return IDENTITY_FIELD_IDS.every((fieldId) => {
    const value = formData[fieldId];
    return typeof value === "string" && value.trim().length > 0;
  });
}

/** Dynamic sections after identity + carrier selection (schema v3). */
export function getDynamicSectionIds(
  carriers: string[] = [],
  options: { portal?: boolean } = {}
) {
  const schema = options.portal
    ? buildPortalContractSchemaForCarriers(carriers)
    : buildFormSchemaForCarriers(carriers);
  // Public: identity + carriers. Portal: carriers only.
  const offset = options.portal ? 1 : 2;
  return schema.sections.slice(offset).map((section) => section.id);
}

export function pruneFormDataForCarriers(
  formData: Record<string, unknown>,
  oldCarriers: string[],
  newCarriers: string[],
  options: { portal?: boolean } = {}
): Record<string, unknown> {
  const oldSchema = options.portal
    ? buildPortalContractSchemaForCarriers(oldCarriers)
    : buildFormSchemaForCarriers(oldCarriers);
  const fieldIdsToRemove = new Set<string>();
  const offset = options.portal ? 1 : 2;

  // Carrier selection determines every section after the identity and carrier
  // steps. Reset those answers when a completed selection changes so no
  // carrier-specific details or signed documents are reused accidentally.
  for (const section of oldSchema.sections.slice(offset)) {
    for (const field of section.fields) {
      fieldIdsToRemove.add(field.id);
    }
  }

  const next: Record<string, unknown> = { ...formData, selectedCarriers: newCarriers };
  for (const fieldId of fieldIdsToRemove) {
    delete next[fieldId];
  }
  return next;
}

/**
 * Remap draft steps saved against the public schema (identity at step 0)
 * onto the portal schema (carriers at step 0).
 */
export function remapPortalDraftStep(
  formData: Record<string, unknown>,
  currentStep: number
): number {
  if (formData.formVariant === PORTAL_FORM_VARIANT) return currentStep;
  return Math.max(0, currentStep - 1);
}

/**
 * Migrate pre-v3 drafts where step 0 combined identity + carriers into separate steps.
 */
export function normalizeDraft(draft: {
  formData: Record<string, unknown>;
  currentStep: number;
}): NormalizedDraft {
  const { formData } = draft;
  let { currentStep } = draft;

  backfillFullName(formData);

  const preV3 =
    draft.currentStep >= 1 ||
    (draft.currentStep === 0 && hasSelectedCarriers(formData));

  if (!preV3) {
    return { formData, currentStep };
  }

  if (draft.currentStep >= 1) {
    currentStep = draft.currentStep + 1;
  } else if (draft.currentStep === 0 && identityComplete(formData) && hasSelectedCarriers(formData)) {
    currentStep = 1;
  }

  return { formData, currentStep };
}
